// =============================================================================
// ActiveSessions — "Where you're signed in" (Settings → Account)
// =============================================================================
// Lists the user's live sessions (GET /api/auth/sessions) with a per-row
// Sign out and a "Sign out all other sessions" button. Signing out another
// session closes its socket server-side; that device lands on the login screen
// via the auth-lost path in App.tsx. "All other" also returns a fresh token for
// this device (the server sets a per-user cutoff that would otherwise end this
// session too), which we must swap in before anything else hits the API.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { api, setToken } from '../api';
import type { ActiveSession } from '../types';
import { Button, Icon, Spinner } from './ui';
import { ConfirmDialog } from './ConfirmDialog';
import { toast } from './Toast';

/** "Chrome on Windows", "Desktop app on macOS", "Safari on iPhone". */
export function describeUserAgent(ua: string | null): { label: string; mobile: boolean } {
  if (!ua) return { label: 'Unknown device', mobile: false };
  const os =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  // Order matters: Edge/Opera/Electron UAs also contain "Chrome", and Chrome's
  // contains "Safari".
  const app =
    /Electron\//.test(ua) ? 'Desktop app'
    : /Edg(e|A|iOS)?\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Firefox\/|FxiOS\//.test(ua) ? 'Firefox'
    : /Chrome\/|CriOS\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /curl|python|node|okhttp/i.test(ua) ? 'Script / API client'
    : 'Browser';
  const mobile = os === 'iPhone' || os === 'Android' || /Mobile/.test(ua);
  return { label: os ? `${app} on ${os}` : app, mobile };
}

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5 * 60) return 'Active now';
  if (s < 3600) return `Active ${Math.round(s / 60)} min ago`;
  if (s < 86400) return `Active ${Math.round(s / 3600)} h ago`;
  const d = Math.round(s / 86400);
  return `Active ${d} day${d === 1 ? '' : 's'} ago`;
}

export function ActiveSessions() {
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [legacy, setLegacy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busyAll, setBusyAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.listSessions();
      setSessions(res.sessions);
      setLegacy(res.currentIsLegacy);
    } catch (err) {
      toast.error(err, 'Could not load your sessions');
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(s: ActiveSession) {
    setBusyId(s.id);
    try {
      await api.revokeSession(s.id);
      setSessions((list) => list?.filter((x) => x.id !== s.id) ?? null);
    } catch (err) {
      toast.error(err, 'Could not sign out that session');
    } finally {
      setBusyId(null);
    }
  }

  async function revokeAllOthers() {
    setConfirmAll(false);
    setBusyAll(true);
    try {
      const res = await api.revokeOtherSessions();
      setToken(res.token);
      toast.success(
        res.revoked === 1 ? 'Signed out 1 other session' : `Signed out ${res.revoked} other sessions`
      );
      await load();
    } catch (err) {
      toast.error(err, 'Could not sign out other sessions');
    } finally {
      setBusyAll(false);
    }
  }

  // Current first, then most recently active (server already orders by that).
  const ordered = sessions ? [...sessions].sort((a, b) => Number(b.current) - Number(a.current)) : null;
  const others = ordered?.filter((s) => !s.current).length ?? 0;

  return (
    <div className="border-t border-border-soft pt-3 mt-4">
      <div className="text-sm text-text font-medium mb-1">Where you're signed in</div>
      <div className="text-xs text-text-muted mb-3">
        Sign out any device you don't recognize. It's signed out immediately.
      </div>

      {ordered === null ? (
        <div className="py-3 flex justify-center text-text-muted">
          <Spinner size={16} />
        </div>
      ) : (
        <ul className="space-y-1.5" aria-label="Active sessions">
          {legacy && (
            <li className="text-xs text-text-muted bevel-down p-2">
              This device signed in before session tracking existed, so it isn't listed. Signing out
              all other sessions will still sign out everything except this device.
            </li>
          )}
          {ordered.map((s) => {
            const { label, mobile } = describeUserAgent(s.userAgent);
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 bevel-down p-2 min-w-0">
                <Icon name={mobile ? 'smartphone' : 'monitor'} size={18} className="shrink-0 text-text-muted" />
                {/* Wraps rather than truncates: in the phone-width Settings
                    column a truncated row reads "Safa…". */}
                <div className="flex-1 min-w-[8rem]">
                  <div className="text-xs text-text font-medium wrap-anywhere">
                    {label}
                    {s.current && (
                      <span className="ml-2 inline-block text-[10px] font-semibold uppercase text-online">This device</span>
                    )}
                  </div>
                  <div
                    className="text-[11px] text-text-muted wrap-anywhere"
                    title={`Signed in ${new Date(s.createdAt).toLocaleString()}`}
                  >
                    {s.current ? 'Active now' : timeAgo(s.lastActiveAt)}
                    {s.ip && <> · {s.ip}</>}
                  </div>
                </div>
                {!s.current && (
                  <Button
                    type="button"
                    onClick={() => void revoke(s)}
                    disabled={busyId === s.id}
                    aria-label={`Sign out ${label}`}
                    className="shrink-0"
                  >
                    {busyId === s.id ? <Spinner size={12} /> : 'Sign out'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {ordered !== null && (others > 0 || legacy) && (
        <div className="pt-3">
          <Button
            type="button"
            variant="danger"
            onClick={() => setConfirmAll(true)}
            disabled={busyAll}
            className="max-w-full whitespace-normal"
          >
            {busyAll ? <Spinner size={14} /> : 'Sign out all other sessions'}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmAll}
        title="Sign out all other sessions?"
        body="Every other device and browser signed in to your account will be signed out immediately. This device stays signed in."
        confirmLabel="Sign out others"
        destructive
        onConfirm={() => void revokeAllOthers()}
        onCancel={() => setConfirmAll(false)}
      />
    </div>
  );
}
